/**
 * Contact Us — /business/contact-us
 * Split-screen layout: left panel with brand info + contact channels,
 * right panel with a multi-field inquiry form.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Phone,
  MapPin,
  Clock,
  Building2,
  MessageSquare,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';
import { B2BMarketingPageLayout } from '../_shared/B2BMarketingPageLayout';

/* ─────────────────────────────────── types ─────────────────────────────── */

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  teamSize: string;
  inquiry: string;
  message: string;
}

type Status = 'idle' | 'submitting' | 'success' | 'error' | 'validation';

/* ─────────────────────────────── static data ───────────────────────────── */

const TEAM_SIZES = [
  '1 – 10',
  '11 – 50',
  '51 – 200',
  '201 – 500',
  '501 – 1,000',
  '1,000+',
];

const INQUIRY_TYPES = [
  { value: 'demo', label: 'Book a product demo' },
  { value: 'support', label: 'Technical support' },
  { value: 'pricing', label: 'Pricing & plans' },
  { value: 'partnership', label: 'Partnership or integration' },
  { value: 'press', label: 'Press & media' },
  { value: 'other', label: 'Something else' },
];

const CHANNELS = [
  {
    icon: Mail,
    label: 'Sales',
    value: 'sales@gograbjob.com',
    href: 'mailto:sales@gograbjob.com',
    note: 'For new business & demos',
  },
  {
    icon: MessageSquare,
    label: 'Support',
    value: 'support@gograbjob.com',
    href: 'mailto:support@gograbjob.com',
    note: 'For existing customers',
  },
  {
    icon: ExternalLink,
    label: 'Partnerships',
    value: 'partners@gograbjob.com',
    href: 'mailto:partners@gograbjob.com',
    note: 'Integrations & co-marketing',
  },
];

const RESPONSE_TIMES = [
  { icon: Clock, text: 'Sales enquiries — same business day' },
  { icon: Clock, text: 'Support tickets — within 4 hours (Pro/Enterprise)' },
  { icon: MapPin, text: 'Remote-first · India & global coverage' },
];

/* ───────────────────────────────── helpers ─────────────────────────────── */

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <p className="mt-1 text-xs text-red-500">{msg}</p>;
}

function inputClass(hasError?: boolean) {
  return [
    'w-full rounded-lg border px-4 py-2.5 text-sm text-gray-900',
    'placeholder-gray-400 outline-none transition-all',
    'focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500',
    'dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder-zinc-500',
    hasError
      ? 'border-red-400 focus:border-red-400 focus:ring-red-300/30'
      : 'border-gray-200 dark:border-zinc-700',
  ].join(' ');
}

/* ──────────────────────────────── component ────────────────────────────── */

export function ContactUsPage() {
  const [form, setForm] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    teamSize: '',
    inquiry: '',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [status, setStatus] = useState<Status>('idle');

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => { const n = { ...prev }; delete n[name as keyof FormData]; return n; });
    }
    if (status !== 'idle') setStatus('idle');
  }

  function validate(): boolean {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required.';
    if (!form.email.trim()) {
      e.email = 'Work email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = 'Please enter a valid email address.';
    }
    if (!form.company.trim()) e.company = 'Company name is required.';
    if (!form.inquiry) e.inquiry = 'Please select an inquiry type.';
    if (!form.message.trim()) {
      e.message = 'Message is required.';
    } else if (form.message.trim().length < 15) {
      e.message = 'Message must be at least 15 characters.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) { setStatus('validation'); return; }
    setStatus('submitting');
    await new Promise((r) => setTimeout(r, 1500));
    setStatus('success');
    setTimeout(() => {
      setForm({ firstName: '', lastName: '', email: '', company: '', teamSize: '', inquiry: '', message: '' });
      setErrors({});
      setStatus('idle');
    }, 4000);
  }

  return (
    <B2BMarketingPageLayout>
      <div className="flex min-h-[calc(100vh-72px)] flex-col lg:flex-row">

        {/* ── LEFT PANEL ── */}
        <div
          className="relative flex flex-col justify-between overflow-hidden px-8 py-12 lg:sticky lg:top-[72px] lg:h-[calc(100vh-72px)] lg:w-[42%] lg:overflow-y-auto"
          style={{ background: 'linear-gradient(145deg, #3b0764 0%, #4c1d95 40%, #5b21b6 70%, #6d28d9 100%)' }}
        >
          {/* decorative blobs */}
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-violet-400/10 blur-3xl" />

          <div className="relative z-10">
            {/* tag */}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-violet-200 backdrop-blur-sm">
              <Building2 className="h-3 w-3" />
              GoGrabJob Business
            </span>

            {/* heading */}
            <h1 className="mt-6 text-3xl font-bold leading-tight text-white lg:text-4xl">
              Let's talk about<br />
              your hiring goals
            </h1>
            <p className="mt-3 text-base leading-relaxed text-violet-200">
              Whether you're evaluating us, need help with your account, or want to explore a partnership — we'll route your message to the right person fast.
            </p>

            {/* channels */}
            <div className="mt-10 space-y-4">
              {CHANNELS.map(({ icon: Icon, label, value, href, note }) => (
                <a
                  key={label}
                  href={href}
                  className="group flex items-start gap-3 rounded-xl bg-white/8 p-4 backdrop-blur-sm transition-colors hover:bg-white/15"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
                    <Icon className="h-4 w-4 text-white" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-violet-300">{label}</p>
                    <p className="text-sm font-medium text-white group-hover:underline">{value}</p>
                    <p className="text-xs text-violet-300">{note}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* response times */}
          <div className="relative z-10 mt-10 space-y-2.5 border-t border-white/10 pt-8">
            {RESPONSE_TIMES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2.5">
                <Icon className="h-3.5 w-3.5 shrink-0 text-violet-300" />
                <span className="text-xs text-violet-200">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="flex flex-1 items-start justify-center bg-gray-50 px-6 py-12 dark:bg-zinc-900 lg:px-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-xl"
          >
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-zinc-50">
              Send us a message
            </h2>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-zinc-400">
              All fields marked <span className="text-red-500">*</span> are required.
            </p>

            {/* success banner */}
            {status === 'success' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-6 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-green-800 dark:border-green-800/50 dark:bg-green-900/20 dark:text-green-300"
              >
                <CheckCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold">Message received!</p>
                  <p className="text-sm">Thanks for reaching out. We'll get back to you within one business day.</p>
                </div>
              </motion.div>
            )}

            {/* validation/error banner */}
            {(status === 'validation' || status === 'error') && (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-6 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300"
              >
                <AlertCircle className="h-5 w-5 shrink-0" />
                <p className="text-sm">
                  {status === 'error'
                    ? 'Something went wrong. Please try again.'
                    : 'Please fix the highlighted fields before submitting.'}
                </p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
              {/* Name row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="Alex"
                    className={inputClass(!!errors.firstName)}
                  />
                  <FieldError msg={errors.firstName} />
                </div>
                <div>
                  <label htmlFor="lastName" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Johnson"
                    className={inputClass()}
                  />
                </div>
              </div>

              {/* Work email */}
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Work email <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="alex@company.com"
                  className={inputClass(!!errors.email)}
                />
                <FieldError msg={errors.email} />
              </div>

              {/* Company + team size */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    autoComplete="organization"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Acme Recruiting"
                    className={inputClass(!!errors.company)}
                  />
                  <FieldError msg={errors.company} />
                </div>
                <div>
                  <label htmlFor="teamSize" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                    Team size
                  </label>
                  <div className="relative">
                    <select
                      id="teamSize"
                      name="teamSize"
                      value={form.teamSize}
                      onChange={handleChange}
                      className={[inputClass(), 'appearance-none pr-9'].join(' ')}
                    >
                      <option value="">Select…</option>
                      {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Inquiry type */}
              <div>
                <label htmlFor="inquiry" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                  How can we help? <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="inquiry"
                    name="inquiry"
                    value={form.inquiry}
                    onChange={handleChange}
                    className={[inputClass(!!errors.inquiry), 'appearance-none pr-9'].join(' ')}
                  >
                    <option value="">Select inquiry type…</option>
                    {INQUIRY_TYPES.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
                <FieldError msg={errors.inquiry} />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your team, your current hiring challenges, and what you're hoping to achieve with GoGrabJob Business…"
                  className={[inputClass(!!errors.message), 'resize-none'].join(' ')}
                />
                <FieldError msg={errors.message} />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'submitting' || status === 'success'}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-violet-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'submitting' ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : status === 'success' ? (
                  <><CheckCircle className="h-4 w-4" /> Sent!</>
                ) : (
                  <><Send className="h-4 w-4" /> Send message</>
                )}
              </button>

              {/* Privacy note */}
              <p className="text-center text-xs text-gray-400 dark:text-zinc-500">
                By submitting, you agree to our{' '}
                <a href="/business/legal/privacy" className="text-violet-600 hover:underline dark:text-violet-400">Privacy Policy</a>
                {' '}and{' '}
                <a href="/business/legal/terms" className="text-violet-600 hover:underline dark:text-violet-400">Terms of Service</a>.
                <br />We never spam. Unsubscribe at any time.
              </p>
            </form>
          </motion.div>
        </div>

      </div>
    </B2BMarketingPageLayout>
  );
}
